import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BillingGroup } from 'common/models/BillingGroup';
import { GetClientBillingGroups } from 'services/ClientsService';
import GridWithStateWrapper from '../GridWidthStateWrapper';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import PreviewLoaderComponent from '../Previews/PreviewLoader';

import { Link } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/material/Box';
import ActionsDropDownButton from '../SummaryBars/Actions/ActionsDropsDownMenu';
import MenuItem from '@mui/material/MenuItem';
import { userHasSinglePermission } from 'util/PermissionsUtils';
import { Permission } from 'common/models/Permissions';

interface Props {
  source: 'client-record';
  sourceId: number;
  setRowCountHandler?: (count: number) => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
  gridName: string;
}

const linkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };

const defaultHiddenCols: GridColumnVisibilityModel = {
  id: false,
  updatedByName: false,
  updatedDate: false,
};

export default function BillingGroupsGridComponent({
  source,
  sourceId,
  setRowCountHandler,
  loadingHandler,
  errorHandler,
  gridName,
}: Props) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rows, setRows] = useState<BillingGroup[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showTagsPrevewNoDelay, setShowTagsPreviewNoDelay] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewEntityType | ''>('');
  const [previewRecordId, setPreviewRecordId] = useState(0);
  const [previewRecordName, setPreviewRecordName] = useState('');
  const apiRef = useGridApiRef();

  const userIsFinancialController = useMemo(
    () => userHasSinglePermission(Permission.FinancialController),
    []
  );

  useEffect(() => {
    const getRows = async () => {
      loadingHandler && loadingHandler(true);
      let res: BillingGroup[] | null = null;
      if (source === 'client-record' && sourceId)
        res = await GetClientBillingGroups(sourceId, errorHandler);
      if (res) {
        setRowCountHandler && setRowCountHandler(res.length);
        setRows(res);
      }
      loadingHandler && loadingHandler(false);
    };
    getRows();
  }, [source, sourceId, setRowCountHandler, loadingHandler, errorHandler]);

  const columns = useMemo<GridColDef[]>(() => {
    const handlePreviewHover = (
      type: PreviewEntityType | '',
      id: number,
      recordName: string = '',
      noDelay: boolean = false
    ) => {
      setPreviewType(type);
      setPreviewRecordId(id);
      setPreviewRecordName(recordName);
      setIsPreviewOpen(true);
      setShowTagsPreviewNoDelay(noDelay);
    };

    const handlePreviewClose = () => {
      setIsPreviewOpen(false);
      setShowTagsPreviewNoDelay(false);
    };

    const linkToBillingGroupRenderer = (params: GridRenderCellParams) => {
      return (
        <Link
          to={`/billing-groups/${params.row.id}`}
          style={linkStyle}
          //onMouseEnter={() => handlePreviewHover('billing-group', params.row.id)}
          onMouseLeave={handlePreviewClose}
          target={'_self'}
        >
          {params.value}
        </Link>
      );
    };

    const linkToContactRenderer = (params: GridRenderCellParams) => {
      const contactId = params.row.primaryContactID as number;
      if (contactId) {
        return (
          <Link
            to={`/contacts/${contactId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover('contact', contactId)}
            onMouseLeave={handlePreviewClose}
          >
            {params.value}
          </Link>
        );
      }
      return params.value;
    };

    const addressRenderer = (params: GridRenderCellParams) => {
      return `${params.row.address1 ?? ''} ${params.row.address2 ?? ''} ${params.row.address3 ?? ''}`;
    };

    const dateValueGetter: GridValueGetter<BillingGroup, any, undefined, string> = (value) => {
      if (value) {
        const d = moment(value);
        if (d.isValid() && d.get('year') > 1) {
          return d.toDate();
        }
      }
    };

    const dateRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return moment(params.value).format('DD MMM YYYY');
      }
    };

    const cols: GridColDef[] = [
      { headerName: 'ID', field: 'id', width: 75, renderCell: linkToBillingGroupRenderer },
      { headerName: 'Name', field: 'name', width: 300, renderCell: linkToBillingGroupRenderer },
      {
        headerName: 'Primary Contact',
        field: 'primaryContactName',
        width: 200,
        renderCell: linkToContactRenderer,
      },
      {
        headerName: 'Address',
        field: 'address',
        headerAlign: 'center',
        align: 'center',
        width: 300,
        renderCell: addressRenderer,
      },
      { headerName: 'Suburb', field: 'suburb', headerAlign: 'center', align: 'center', width: 120 },
      { headerName: 'State', field: 'state', headerAlign: 'center', align: 'center', width: 120 },
      {
        headerName: 'Country',
        field: 'countryName',
        headerAlign: 'center',
        align: 'center',
        width: 120,
      },
      {
        headerName: 'Updated By',
        field: 'updatedByName',
        headerAlign: 'center',
        align: 'center',
        width: 200,
      },
      {
        headerName: 'Updated Date',
        field: 'updatedDate',
        headerAlign: 'center',
        align: 'center',
        width: 110,
        type: 'date',
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
      },
    ];

    return cols;
  }, [apiRef, userIsFinancialController, source]);

  const exportToExcel = useCallback(() => {
    const api = apiRef.current;
    if (api) api.exportDataAsExcel();
  }, [apiRef]);

  const gridActions = useMemo(() => {
    return (
      <>
        {userIsFinancialController && (
          <ActionsDropDownButton color="secondary" label="List Actions" ml="5px">
            {userIsFinancialController && Boolean(sourceId) && (
              <MenuItem>
                <Link
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  to={`/billing-groups/create?ClientID=${sourceId}`}
                >
                  Add Billing Group
                </Link>
              </MenuItem>
            )}
            {userIsFinancialController && <MenuItem onClick={exportToExcel}>Export</MenuItem>}
          </ActionsDropDownButton>
        )}
      </>
    );
  }, [userIsFinancialController, sourceId, exportToExcel, selectionModel]);

  return (
    <>
      <PreviewLoaderComponent
        open={isPreviewOpen}
        entityType={previewType}
        recordId={previewRecordId}
        isTagsPreview={false}
        showDelayMs={showTagsPrevewNoDelay ? 0 : undefined}
        titleOverride={previewRecordName}
        kioskToken={null}
      />
      {Boolean(gridActions) && (
        <Box pb="10px" ml="auto">
          {gridActions}
        </Box>
      )}
      <GridWithStateWrapper
        gridName={gridName}
        defaultViewModel={defaultHiddenCols}
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(sm) => setSelectionModel(sm)}
        pagination
        pageSizeOptions={[100, 250, 500, 1000]}
      />
    </>
  );
}
